import { useTranslation } from 'react-i18next'

import { Box, Flex, Text } from '@enechain/ecloud-designsystem'

import {
  COMPACT_DATE_FIELD_WIDTH,
  CompactCivilDateField,
} from '~/components/common/CompactCivilDateField'
import { WEEKDAY_KEYS } from '~/components/common/DayGrid'
import { HiddenFieldLabel } from '~/components/common/HiddenFieldLabel'
import {
  CollapsibleFilterToolbar,
  FilterGrid,
} from '~/components/common/filters'
import { type CivilDate, civilDateToDate } from '~/models/civilDate'
import { formatDateDisplay } from '~/models/dateTime'

const PERIOD_FROM_INPUT_ID = 'jepxSettlement-periodFrom'
const PERIOD_TO_INPUT_ID = 'jepxSettlement-periodTo'

/** 開閉の保存キー (CollapsibleFilterToolbar が接頭辞を足す)。 */
const JEPX_SETTLEMENT_FILTER_STORAGE_KEY = 'jepxSettlement'

type Props = {
  from: CivilDate | undefined
  to: CivilDate | undefined
  onFromChange: (d: CivilDate | undefined) => void
  onToChange: (d: CivilDate | undefined) => void
  onClear: () => void
}

/**
 * 清算日の期間の絞り込み (AC-JST-002)。 開閉式 (UI 辞書 toolbar-collapsible.md「開閉式」)。
 * 指定中の期間と件数は、 ツールバーの下の `PeriodSummary` に出す (8080 `bidding/settlement.html` の
 * `.stl-summary`)。 閉じた行の要約にも清算日の今の値を出す (未指定は「すべて」)。
 *
 * 見出し「清算日」は入力と同じ行に置く (UI 辞書 `components/toolbar.md` の
 * `.filter-group` = ラベル + コントロールの横並び。 8080 も同じ)。 「クリア」は閉じた行の右端
 * (期間が指定されている時だけ押せる。 件数 1 = 清算日)。 [画面側の判断] 清算日はこの画面の唯一の条件で、
 * 既定は「すべて」 (毎回変わる表示日ではない) ので件数に数え、 「クリア」で戻す (既存の「期間をクリア」と同じ範囲)。
 *
 * 開始日・終了日 (どちらも含む) の 2 つの日付入力で、 各々任意。 初期は期間指定なし
 * (= すべて)。 期間はサーバー側で絞る (request の `settlement_date_from` / `_to`)。
 *
 * **開始日 > 終了日は入力の時点で作らせない**: 終了日の `min` に開始日、 開始日の `max` に
 * 終了日を渡す (`CompactCivilDateField` は popup で範囲外の日を選べなくし、 手入力された
 * 範囲外の値は境界へ寄せる)。 供給力ギャップ AC-SDG-008 (「終了日が開始日より前なら
 * 終了日を開始日に揃える」) と同じ向き。 backend は開始日 > 終了日を InvalidArgument にするので、
 * 画面がその request を送らないことが前提になる。
 */
export const PeriodFilter = ({
  from,
  to,
  onFromChange,
  onToChange,
  onClear,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const isPeriodSet = from !== undefined || to !== undefined
  const periodLabel = t('page:jepxSettlement.filter.period')

  // 要約の日付は日付欄と同じ `YYYY/MM/DD (曜)`。 期間は `〜` でつなぎ、 片側だけなら片側だけ
  const summaryDate = (d: CivilDate | undefined): string => {
    if (d === undefined) return ''
    const date = civilDateToDate(d)
    return t('common:date.withWeekday', {
      date: formatDateDisplay(date),
      weekday: t(`common:date.calendar.weekday.${WEEKDAY_KEYS[date.getDay()]}`),
    })
  }

  return (
    <CollapsibleFilterToolbar
      modifiedCount={isPeriodSet ? 1 : 0}
      onClear={onClear}
      storageKey={JEPX_SETTLEMENT_FILTER_STORAGE_KEY}
      summary={[
        {
          key: 'period',
          label: periodLabel,
          value: isPeriodSet
            ? `${summaryDate(from)}〜${summaryDate(to)}`
            : t('common:filter.all'),
        },
      ]}
    >
      <FilterGrid
        items={[
          {
            key: 'period',
            label: periodLabel,
            control: (
              <Flex alignItems="center" flexWrap="wrap" gap={2}>
                <Box width={COMPACT_DATE_FIELD_WIDTH}>
                  <HiddenFieldLabel htmlFor={PERIOD_FROM_INPUT_ID}>
                    {t('page:jepxSettlement.filter.periodFrom')}
                  </HiddenFieldLabel>
                  <CompactCivilDateField
                    hasClear
                    id={PERIOD_FROM_INPUT_ID}
                    max={to}
                    onChange={onFromChange}
                    value={from}
                  />
                </Box>
                <Text color="Text.midEmphasis">〜</Text>
                <Box width={COMPACT_DATE_FIELD_WIDTH}>
                  <HiddenFieldLabel htmlFor={PERIOD_TO_INPUT_ID}>
                    {t('page:jepxSettlement.filter.periodTo')}
                  </HiddenFieldLabel>
                  <CompactCivilDateField
                    hasClear
                    id={PERIOD_TO_INPUT_ID}
                    min={from}
                    onChange={onToChange}
                    value={to}
                  />
                </Box>
              </Flex>
            ),
          },
        ]}
      />
    </CollapsibleFilterToolbar>
  )
}
